import React from 'react';
import { Edit, Plus, Trash2, X } from 'lucide-react';

import { formatIST } from '@/lib/utils';
import { getProductTypeName } from '@/lib/constants';
import {
  ActionRow, BottomSheet, Card, PrimaryButton, StatusPill, TopBar, PhonePage,
} from '@/components/phone/primitives';
import { INPUT_CLASS, inputStyle, SelectField } from '@/components/phone/FieldControls';
import { Colors } from '@/components/phone/theme';

/**
 * One product, on a phone.
 *
 * The desktop opens with a bar of eight facts and puts the prices and movements
 * in a rail beside the specification. At a handset's width the bar wrapped into
 * four rows and the rail fell under everything, so here the bar names the
 * product, a pill says how much is on the shelf, the actions are one row, and
 * the facts, specification, prices and movements are cards in reading order.
 */

const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

function Fact({ label, value, tone }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-medium uppercase tracking-[0.5px]" style={{ color: Colors.text.secondary }}>{label}</p>
      <p className="mt-0.5 truncate text-[14px] font-semibold" style={{ color: tone || Colors.text.primary }}>{value || '—'}</p>
    </div>
  );
}

export default function PhoneCatalogItem({
  item, transactions, customers, getFieldLabel,
  onBack, onEdit, onDelete, onRemovePrice,
  priceSheetOpen, onOpenPriceSheet, onClosePriceSheet, newPrice, setNewPrice, onSubmitPrice,
}) {
  const stock = item.finished_goods_stock || 0;
  const threshold = item.min_stock_threshold || 0;
  const stockTone = stock === 0 ? 'critical' : threshold > 0 && stock <= threshold ? 'caution' : 'positive';
  const stockColor = { critical: Colors.status.error, caution: Colors.status.warning, positive: undefined }[stockTone];

  const orderFields = Object.entries(item.order_creation_fields || {})
    .filter(([, v]) => v !== null && v !== undefined && v !== '');
  const jobCardFields = Object.entries(item.job_card_fields || {})
    .filter(([, v]) => v !== null && v !== undefined && v !== '');

  const specs = (entries, isJobCard) => (
    <div className="grid grid-cols-2 gap-x-3 gap-y-3">
      {entries.map(([key, value]) => (
        <Fact key={key} label={getFieldLabel(key, isJobCard)} value={Array.isArray(value) ? value.join(', ') : String(value)} />
      ))}
    </div>
  );

  const customerOptions = customers
    .filter((c) => !item.customer_prices?.some((cp) => cp.customer_id === c.id))
    .map((c) => ({ value: c.id, label: `${c.customer_name}${c.firm_name ? ` — ${c.firm_name}` : ''}` }));

  return (
    <PhonePage testID="catalog-item-detail-page">
      <TopBar
        eyebrow={`CATALOG · ${getProductTypeName(item.product_type, true)}`}
        title={item.name}
        subtitle={item.customer_name || 'General'}
        onBack={onBack}
      />
      <div className="border-b px-4 pt-2" style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill testID="stock-pill" label={`${stock.toLocaleString()} in stock`} tone={stockTone} dot />
          <StatusPill label={item.status || 'active'} tone={item.status === 'inactive' ? 'neutral' : 'accent'} />
        </div>
        <ActionRow
          actions={[
            { key: 'price', label: 'Add customer price', icon: Plus, onClick: onOpenPriceSheet, testID: 'add-price-btn' },
            { key: 'edit', label: 'Edit', icon: Edit, onClick: onEdit, primary: true, testID: 'edit-item-btn' },
            { key: 'delete', label: 'Delete', icon: Trash2, onClick: onDelete, tone: 'critical', testID: 'delete-item-btn' },
          ]}
        />
      </div>

      <div className="flex-1 px-4 pb-10">
        <Card className="mt-3" testID="item-facts">
          <div className="grid grid-cols-2 gap-x-3 gap-y-3">
            <Fact label="Code" value={item.code || item.id?.slice(0, 8)} />
            <Fact label="Customer" value={item.customer_name || 'General'} />
            <Fact label="Stock" value={stock.toLocaleString()} tone={stockColor} />
            <Fact label="Minimum" value={threshold || '—'} />
            <Fact label="Default price" value={item.default_price ? money(item.default_price) : '—'} />
            <Fact label="Status" value={item.status || 'active'} />
            <Fact label="Created" value={item.created_at ? formatIST(item.created_at, { dateOnly: true }) : '—'} />
            <Fact label="Updated" value={item.updated_at ? formatIST(item.updated_at, { dateOnly: true }) : '—'} />
          </div>
        </Card>

        <Card className="mt-3" title="Specifications" aside="carries onto every order">
          {orderFields.length > 0 && (
            <>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.5px]" style={{ color: Colors.text.tertiary }}>
                Asked at order entry
              </p>
              {specs(orderFields, false)}
            </>
          )}
          {jobCardFields.length > 0 && (
            <>
              <p className={`mb-2 text-[10px] font-semibold uppercase tracking-[0.5px] ${orderFields.length ? 'mt-4' : ''}`} style={{ color: Colors.text.tertiary }}>
                Filled in on the job card
              </p>
              {specs(jobCardFields, true)}
            </>
          )}
          {orderFields.length === 0 && jobCardFields.length === 0 && (
            <p className="py-6 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>
              No specifications saved on this product yet.
            </p>
          )}
        </Card>

        <Card className="mt-3" title="Customer prices" aside={`${item.customer_prices?.length || 0}`} testID="customer-prices">
          {item.customer_prices?.length ? item.customer_prices.map((cp) => (
            <div key={cp.customer_id} className="flex items-center justify-between gap-2 border-b py-2 last:border-b-0" style={{ borderColor: Colors.border.subtle }}>
              <span className="min-w-0">
                <span className="block truncate text-[14px]" style={{ color: Colors.text.primary }}>{cp.customer_name}</span>
                <span className="block text-[11px]" style={{ color: Colors.text.tertiary }}>per piece</span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <span className="text-[14px] font-bold tabular-nums" style={{ color: Colors.text.primary }}>{money(cp.price_per_piece)}</span>
                <button
                  type="button"
                  onClick={() => onRemovePrice(cp.customer_id)}
                  aria-label={`Remove price for ${cp.customer_name}`}
                  className="flex h-9 w-9 items-center justify-center rounded-full border"
                  style={{ borderColor: Colors.border.default, color: Colors.status.error }}
                >
                  <X className="h-4 w-4" />
                </button>
              </span>
            </div>
          )) : (
            <p className="py-4 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>
              No customer-specific prices — everyone pays the default.
            </p>
          )}
        </Card>

        <Card className="mt-3" title="Stock movements" aside={`${transactions.length}`}>
          {transactions.length === 0 ? (
            <p className="py-4 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>No movements recorded</p>
          ) : transactions.map((t, i) => {
            const added = (t.adjustment_type || t.type) === 'add';
            return (
              <div key={t.id || i} className="flex items-start justify-between gap-2 border-b py-2 last:border-b-0" style={{ borderColor: Colors.border.subtle }}>
                <span className="min-w-0">
                  <span className="block truncate text-[13px]" style={{ color: Colors.text.primary }}>
                    {(t.reason || t.type || '').replace(/_/g, ' ')}
                  </span>
                  <span className="block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
                    {t.created_by_name || '—'}
                    {t.created_at ? ` · ${formatIST(t.created_at, { compact: true })}` : ''}
                  </span>
                </span>
                <span className="shrink-0 text-[13px] font-bold tabular-nums" style={{ color: added ? Colors.semantic.positive : Colors.semantic.critical }}>
                  {added ? '+' : '−'}{t.quantity}
                </span>
              </div>
            );
          })}
        </Card>
      </div>

      <BottomSheet open={priceSheetOpen} onClose={onClosePriceSheet} title="Add customer price" subtitle={item.name} testID="price-sheet">
        <p className="mb-1.5 text-[11px] font-medium" style={{ color: Colors.text.secondary }}>Customer</p>
        <SelectField
          name="customer" label="Customer" value={newPrice.customer_id} options={customerOptions}
          onChange={(v) => setNewPrice((p) => ({ ...p, customer_id: v }))} placeholder="Pick a customer"
        />
        <p className="mb-1.5 mt-3 text-[11px] font-medium" style={{ color: Colors.text.secondary }}>Price per piece</p>
        <input
          type="number" inputMode="decimal" value={newPrice.price_per_piece} placeholder="0.00"
          onChange={(e) => setNewPrice((p) => ({ ...p, price_per_piece: e.target.value }))}
          data-testid="price-per-piece" className={`${INPUT_CLASS} text-right tabular-nums`} style={inputStyle}
        />
        <p className="mt-3 text-[11px]" style={{ color: Colors.text.tertiary }}>
          This price is used instead of the default whenever this customer orders this product.
        </p>
        <PrimaryButton className="mt-4" onClick={onSubmitPrice} testID="submit-price-btn">Add price</PrimaryButton>
      </BottomSheet>
    </PhonePage>
  );
}
